// Arcade — one game, one XP window. Two engines:
//  • DOS mode (default for the MS-DOS titles in Games/catalog.js DOS): our own page /dos.html runs js-dos
//    (DOSBox in WebAssembly) with the game zip streamed from the Internet Archive's CORS endpoint, so the
//    keyboard is ours — WASD walks, E uses (Kris, 2026-09-08: "use WASD to walk… mouse is annoying").
//  • Archive mode: the Internet Archive's own player in an iframe (archive.org/embed/<id>) for Windows 3.1
//    titles and the giant zips; also the automatic fallback if DOS mode fails to fetch or boot.
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { WindowDropDowns } from 'components';
import { embedOf, pageOf, dosOf, dosUrl } from '../Games/catalog';

const menus = (dos, wasd, canDos) => ({
  Game: [
    { type: 'item', text: 'Restart', hotkey: 'F2' },
    { type: 'item', text: 'Full Screen', hotkey: 'F4' },
    { type: 'item', text: 'Open on archive.org' },
    { type: 'separator' },
    { type: 'item', text: 'Exit' },
  ],
  Controls: [
    { type: 'item', text: 'WASD walks (W A S D + E)', symbol: dos && wasd ? 'check' : undefined, disable: !dos },
    { type: 'item', text: "Game's own keys (arrows)", symbol: dos && !wasd ? 'check' : undefined, disable: !dos },
    { type: 'separator' },
    { type: 'item', text: 'Engine: DOS in this window', symbol: dos ? 'circle' : undefined, disable: !canDos },
    { type: 'item', text: "Engine: Internet Archive's player", symbol: !dos ? 'circle' : undefined },
  ],
  Help: [
    { type: 'item', text: 'Controls' },
    { type: 'item', text: 'About' },
  ],
});

function Arcade({ onClose, game, isFocus }) {
  const meta = dosOf(game);
  const canDos = !!meta;
  const [engine, setEngine] = useState(canDos ? 'dos' : 'ia');
  const [wasd, setWasd] = useState(meta ? meta.wasd : false);
  const [nonce, setNonce] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [ready, setReady] = useState(false);
  const [dialog, setDialog] = useState(null); // 'about' | 'controls'
  const [note, setNote] = useState('');
  const frameRef = useRef(null);
  const dos = engine === 'dos';
  const frameId = `ic-arcade-${game ? game.id : 'none'}-${nonce}`;

  useEffect(() => {
    setLoaded(false);
    setReady(false);
  }, [nonce, engine]);

  // dos.html talks back: ready → hide the loader; error → fall back to the Archive's player.
  useEffect(() => {
    if (!game) return undefined;
    function onMsg(e) {
      if (e.origin !== window.location.origin || !e.data || e.data.id !== game.ia) return;
      if (e.data.type === 'dos-ready') setReady(true);
      if (e.data.type === 'dos-error') {
        setNote(`DOS mode failed (${e.data.msg}) — switched to the Internet Archive's player`);
        setEngine('ia');
        setNonce(n => n + 1);
      }
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [game]);

  // keep the WASD flag in the running emulator in sync (no reboot needed)
  useEffect(() => {
    if (!dos) return;
    try {
      const w = frameRef.current && frameRef.current.contentWindow;
      if (w && w.__dos) w.__dos.setWasd(wasd);
    } catch (e) {
      /* cross-origin only if something is very wrong */
    }
  }, [wasd, dos, ready]);

  if (!game) {
    return (
      <Div>
        <div className="ar__empty">
          <b>No game selected.</b>
          <p>Open the <span className="ar__link" onClick={() => window.dispatchEvent(new CustomEvent('ic:open', { detail: 'Games' }))}>Games</span> folder on the desktop and double-click one.</p>
        </div>
      </Div>
    );
  }

  const src = dos ? dosUrl(game, wasd) : embedOf(game);

  function fullScreen() {
    const f = document.getElementById(frameId);
    if (f && f.requestFullscreen) f.requestFullscreen();
  }
  function onClickOptionItem(item) {
    switch (item) {
      case 'Exit':
        onClose();
        break;
      case 'Restart':
        setNonce(n => n + 1);
        break;
      case 'Full Screen':
        fullScreen();
        break;
      case 'Open on archive.org':
        window.open(pageOf(game), '_blank', 'noopener');
        break;
      case 'WASD walks (W A S D + E)':
        setWasd(true);
        break;
      case "Game's own keys (arrows)":
        setWasd(false);
        break;
      case 'Engine: DOS in this window':
        if (canDos && !dos) { setEngine('dos'); setNonce(n => n + 1); setNote(''); }
        break;
      case "Engine: Internet Archive's player":
        if (dos) { setEngine('ia'); setNonce(n => n + 1); setNote(''); }
        break;
      case 'Controls':
        setDialog(d => (d === 'controls' ? null : 'controls'));
        break;
      case 'About':
        setDialog(d => (d === 'about' ? null : 'about'));
        break;
      default:
    }
  }

  const showLoader = dos ? !ready && !loaded : !loaded;
  return (
    <Div>
      <section className="ar__toolbar">
        <WindowDropDowns items={menus(dos, wasd, canDos)} onClickItem={onClickOptionItem} />
      </section>
      <div className="ar__screen">
        {showLoader && (
          <div className="ar__loading">
            <div className="ar__loading__title">{game.title}</div>
            <div className="ar__loading__sub">{game.year} · {game.by}</div>
            <div className="ar__loading__bar"><i /></div>
            <div className="ar__loading__hint">{dos ? `loading DOSBox… the game streams from the Internet Archive (${meta.mb} MB)` : "loading the Internet Archive's emulator…"}</div>
          </div>
        )}
        <iframe
          id={frameId}
          key={`${engine}-${nonce}`}
          ref={frameRef}
          title={game.title}
          src={src}
          allow="fullscreen; autoplay; gamepad; pointer-lock"
          onLoad={() => setLoaded(true)}
          className={`ar__frame${dos ? ' ar__dos' : ''}${loaded ? ' ar__frame--on' : ''}`}
        />
        {dialog && (
          <div className="ar__dialog" onClick={() => setDialog(null)}>
            <div className="ar__dialog__box" onClick={e => e.stopPropagation()}>
              {dialog === 'controls' ? (
                <>
                  <b>{game.title} — controls</b>
                  {dos ? (
                    <>
                      <p>{wasd ? 'W A S D walk, E uses / opens. ' : ''}The game&apos;s own keys: {game.controls}.</p>
                      <p>Click the screen once so it gets your keyboard and mouse. Esc releases the mouse. Switch mappings in the Controls menu.</p>
                    </>
                  ) : (
                    <>
                      <p>{game.controls}</p>
                      <p>Click the power button in the window to boot, then click inside the game once so it gets your keyboard. Windows games boot Windows 3.1 first — give them ~20 seconds. This engine uses the game&apos;s original keys; WASD needs the DOS engine.</p>
                    </>
                  )}
                  <p>Full Screen is in the Game menu (F4 in the real thing).</p>
                </>
              ) : (
                <>
                  <b>{game.title}</b>
                  <p>{game.by}, {game.year}.</p>
                  <p>{dos
                    ? 'Running on DOSBox (js-dos, WebAssembly) inside this window; the game files stream from the Internet Archive’s software library when you open it and are never stored here.'
                    : 'Running on the Internet Archive’s in-browser DOSBox. Nothing here is ours except the window — the game, the emulator and the bytes are archive.org’s software library.'}</p>
                  <p className="ar__q">{game.blurb}</p>
                </>
              )}
              <span className="ar__dialog__ok" onClick={() => setDialog(null)}>OK</span>
            </div>
          </div>
        )}
      </div>
      <div className="ar__status">
        <div className="ar__status__cell ar__status__cell--grow" title={game.controls}>
          {note || (dos ? (wasd ? 'WASD walks · E uses · click the screen to grab the mouse · Esc lets go' : `${game.controls} · click the screen first`) : game.controls)}
        </div>
        <div className="ar__status__cell">{dos ? 'DOSBox in this window · files from the Internet Archive' : 'emulated by the Internet Archive'}</div>
      </div>
    </Div>
  );
}

const Div = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ece9d8;
  .ar__toolbar { height: 22px; flex-shrink: 0; border-bottom: 1px solid rgba(0,0,0,.15); padding-left: 2px; }
  .ar__screen { flex: 1; min-height: 0; position: relative; background: #000; }
  .ar__frame { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; opacity: 0; transition: opacity .4s; background: #000; }
  .ar__frame--on { opacity: 1; }
  .ar__loading { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: #ccff00; font-family: Tahoma, 'Noto Sans', sans-serif; font-size: 11px; text-align: center; padding: 20px; pointer-events: none; }
  .ar__loading__title { font-size: 20px; font-weight: 700; color: #fff; text-shadow: 0 0 14px rgba(204,255,0,.7); }
  .ar__loading__sub { color: #b8d73c; }
  .ar__loading__bar { width: 220px; height: 14px; border: 1px solid #7a9a00; background: #111; padding: 2px;
    i { display: block; height: 100%; width: 40%; background: repeating-linear-gradient(90deg, #ccff00 0 8px, transparent 8px 11px); animation: ar-slide 1.2s linear infinite; } }
  @keyframes ar-slide { from { transform: translateX(-40%); } to { transform: translateX(250%); } }
  .ar__loading__hint { color: #9aa860; max-width: 340px; }
  .ar__dialog { position: absolute; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 2; }
  .ar__dialog__box { background: #ece9d8; border: 1px solid #0a246a; box-shadow: 4px 4px 0 rgba(0,0,0,.4); padding: 14px 18px; width: 340px; font-size: 11px; color: #000; p { margin: 6px 0; } .ar__q { font-style: italic; color: #4c6200; } }
  .ar__dialog__ok { display: inline-block; margin-top: 6px; padding: 2px 18px; border: 1px solid #003c74; border-radius: 3px; background: linear-gradient(#fff,#ecebe5); cursor: default; }
  .ar__status { flex-shrink: 0; height: 20px; display: flex; font-size: 11px; border-top: 1px solid rgba(0,0,0,.2); }
  .ar__status__cell { padding: 0 8px; display: flex; align-items: center; border-right: 1px solid rgba(0,0,0,.15); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; &--grow { flex: 1; min-width: 0; } &:last-child { border-right: 0; } }
  .ar__empty { padding: 24px; font-size: 11px; b { display: block; margin-bottom: 6px; } }
  .ar__link { color: #2f4a00; text-decoration: underline; cursor: pointer; }
`;

export default Arcade;
